import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { processTemplateApplyService } from '../services/processTemplateApply.service';
import type { WorkProcessTemplate } from '../types/processTemplate.types';
import type { EstimateItem } from '../../shared/types/estimate.types';

export function useProcessTemplatePreview(templateId?: string, baseQuantity = 0) {
  const query = useQuery({
    queryKey: ['estimates', 'process-template-preview', templateId, baseQuantity],
    queryFn: () => processTemplateApplyService.preview(templateId!, baseQuantity),
    enabled: !!templateId,
  });

  return {
    preview: query.data || null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function useLocalProcessTemplatePreview(template?: WorkProcessTemplate | null, item?: EstimateItem | null) {
  return useMemo(() => {
    if (!template || !item) return null;
    return processTemplateApplyService.buildPreviewFromEstimateItem(template, item);
  }, [template, item]);
}

export function useApplyProcessTemplate(estimateId?: string) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ templateId, itemId, selectedStepIds }: { templateId: string; itemId: string; selectedStepIds?: string[] }) =>
      processTemplateApplyService.applyToEstimateItem({
        user_id: profile?.id || '',
        template_id: templateId,
        estimate_item_id: itemId,
        selected_step_ids: selectedStepIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates', 'items', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates', 'resources', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimates', 'detail', estimateId] });
      toast.success('Đã áp quy trình vào dự toán');
    },
    onError: (error: any) => toast.error(error?.message || 'Không áp được quy trình'),
  });

  return {
    applyProcessTemplate: mutation.mutateAsync,
    isApplying: mutation.isPending,
  };
}
